import { colors as themeColors, greyLightMode } from '@/theme/colors';
import type { SxProps, Theme } from '@mui/material';

// Centralized greys map so the component doesn't need to pull from per-feature
// style files. Matches the same shades used throughout the app.
const palette = {
  primary: themeColors.primary.main,
  gray200: greyLightMode.shade4,
  gray600: greyLightMode.shade8,
  gray700: greyLightMode.shade9,
  gray800: greyLightMode.shade10,
};

// Styles for the rendered HTML block (description, responsibilities, notes etc.).
// Keep selectors :where-free so consuming components can still override per-element
// via their own sx without specificity wars.
export const htmlBlockSx: SxProps<Theme> = {
  color: palette.gray600,
  fontSize: '0.875rem',
  lineHeight: 1.6,
  // Quill / employer payloads use &nbsp; between every word, which the browser
  // treats as non-breaking and refuses to wrap. Force wrapping so long runs
  // (and long URLs/words) never push past the card.
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  '& *': { maxWidth: '100%' },
  '& p, & h1, & h2, & h3, & h4, & h5, & h6, & li, & blockquote': {
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  '& p': { fontSize: '0.79rem', margin: '0.25em 0' },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontSize: '0.79rem !important',
    fontWeight: 700,
    margin: '0.4em 0 0.2em',
    lineHeight: 1.5,
    color: palette.gray800,
  },
  '& strong, & b': { fontWeight: 700, color: palette.gray700 },
  '& em, & i': { fontStyle: 'italic' },
  '& ul': {
    listStyleType: 'disc',
    paddingLeft: '1.25em',
    margin: '0.25em 0',
  },
  '& ol': {
    listStyleType: 'decimal',
    paddingLeft: '1.25em',
    margin: '0.25em 0',
  },
  '& li': { margin: '0.15em 0' },
  '& a': { color: palette.primary, textDecoration: 'underline' },
  '& blockquote': {
    borderLeft: `3px solid ${palette.gray200}`,
    pl: 1.5,
    color: palette.gray600,
    margin: '0.5em 0',
  },
  '& > *:first-of-type': { mt: 0 },
  '& > *:last-of-type': { mb: 0 },
};

export const htmlBlockEmptyTextSx: SxProps<Theme> = {
  color: palette.gray600,
};
